import { Component, OnInit, OnDestroy, Input, ViewChild, ElementRef, HostListener, ViewContainerRef } from '@angular/core';
import { applyFroalaAriaFix, stripFroalaWatermark } from '../../common/utils/froala.utils';
import { FormGroup, FormBuilder, Validators, FormControl } from "@angular/forms";
import { Router } from "@angular/router";
import { DomSanitizer, SafeResourceUrl} from '@angular/platform-browser';
import { Subscription, BehaviorSubject } from "rxjs";

import { AuthService } from './../../services/auth.service';
import { DateService } from "../../common/services/date.service";
import { DocumentManagementService, IUploadFile, UploadedFile } from '../../common/services/document-management.service';
import { LettersService } from "../letters.service";
import { LoaderService } from '../../common/services/loader.service';
import { ReportSelectorService } from "../../form-controls/services/report-selector.service";
import { SubHeaderService } from "../../core/services/sub-header.service";
import { SystemNotificationService } from '../../notifications/system-notifications/services/system-notification.service';

import { AssociateDocumentKind, Letter, LetterAddress, LetterStatus, LetterSearchCriteria, MailDeliveryType, LetterAttachment, LabelSizeKind } from '../letters.model';
import { SubHeader } from "../../core/models/sub-header.model";
import { ValidatorPatterns } from '../../common/classes/validator-patterns';
import { Dialog, DialogButton } from '../../core/models/dialog.model';
import { DynamicDialogService } from '../../core/services/dynamic-dialog.service';
import { mergeMap, tap } from 'rxjs/operators';
import { UploadViewFilesComponent } from '../../common/components/fileUpload/upload-view-files.component';
import { CommonDatePickerComponent } from '../../common/components/common-date-picker/common-date-picker.component';

@Component({
    selector: 'letter-detail',
    templateUrl: './letter-detail.component.html',
    styleUrls: ['./letter-detail.component.scss'],
    standalone: false
})
export class LetterDetailComponent implements OnInit, OnDestroy {

    @ViewChild(UploadViewFilesComponent) public _uploadViewFilesElement: UploadViewFilesComponent;
    @ViewChild('sentDatePicker') public _sentDatePicker: CommonDatePickerComponent;
    @ViewChild('deliveryDatePicker') public _deliveryDatePicker: CommonDatePickerComponent;

    confirmLetterSaveDialog: Dialog = new Dialog("Are you sure you want to save?", [new DialogButton("Save letter", true, () => this.onLetterSaveConfirm()), new DialogButton("Cancel", true, () => { })]);
    stopEditingDialog: Dialog = new Dialog("The preview will not reflect any changes unless you save the letter. Any changes you made will be preserved in the edit view so long as you remain on this page however.",
        [new DialogButton("Ok", true, () => { this.editingLetter = !this.editingLetter; })]);
    @Input() offices: LetterAddress[];
    @Input() set record(value: Letter) {
        //why this code executes twice? Todo: refactor
        this._record = value;
        if (this._record !== undefined && this._record !== null) {

            this.subheaderService.updateSubHeader(new SubHeader({
                title: this._record.title, buttons: []
            }));
            if (!this._record.recipient.name2) {
                this._record.recipient.name2 = this._record.organizationName;
            }


            this.saveTrackRre = this._record.trackRRE;
            this.previewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.lettersService.previewUrl(this._record.id, false));
            this.viewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.lettersService.previewUrl(this._record.id, true));
            if (this._record.sentDate)
                this.sentDate = new Date(this._record.sentDate);

            if (this._record.deliveryDate)
                this.deliveryDate = new Date(this._record.deliveryDate);

            this.constructForm();
            this._recordReady.next(true);
            this.editingLetter = false;
            this.loaderService.toggleLoader(false);
        }
    }

    pagesHelpText: string = "Only alter this if the intent is to add pages that weren't already provided as attachments.";

    normalMailDeliveryOptions: any =
        [
            { value: MailDeliveryType.Certified, displayText: "Certified" },
            { value: MailDeliveryType.HandDelivery, displayText: "Hand Delivery" },
            { value: MailDeliveryType.Regular, displayText: "Regular" },
        ];

    chiefsOrderMailDeliveryOptions: any =
        [
            { value: MailDeliveryType.CertificateOfMailing, displayText: "Certificate Of Mailing PS 3817" },
            { value: MailDeliveryType.Certified, displayText: "Certified" },
            { value: MailDeliveryType.HandDelivery, displayText: "Hand Delivery" },
            { value: MailDeliveryType.Other, displayText: "Other" },
            { value: MailDeliveryType.Regular, displayText: "Regular" },
        ];

    private _froalaOptions: any = null;
    get froalaOptions(): any {
        if (!this._froalaOptions) {
            let conf = window["PdfConfig"] as any;
            let key = (conf != null) ? conf.editorLicenseKey : "";
            this._froalaOptions = applyFroalaAriaFix({
                heightMax: 500,
                height: 500,
                heightMin: 150,
                key: key,
                fontSize: ['8', '9', '10', '11', '12', '14', '16', '18', '20', '22', '24', '26', '26.5', '27', '30', '60', '72', '96'],
                fontFamily: {
                    'Arial,Helvetica,sans-serif': 'Arial',
                    'Georgia,serif': 'Georgia',
                    'Impact,Charcoal,sans-serif': 'Impact',
                    'Tahoma,Geneva,sans-serif': 'Tahoma',
                    "'Times New Roman',Times,serif": 'Times New Roman',
                    'Verdana,Geneva,sans-serif': 'Verdana',
                    'Gabriola,sans-serif': 'Gabriola'
                },
                toolbarButtons: ["fullscreen", "html", "|", "undo", "redo", "|", "bold", "italic", "underline", 'strikeThrough', "|", "formatOL", "formatUL", "|", "align", "fontFamily", "fontSize", "paragraphFormat", "insertLink", "|", "outdent", "indent", "clearFormatting", "insertTable", "color"]
            });
        }
        return this._froalaOptions;
    }

    mailDeliveryOptions(): any {
        if (this._record.chiefsOrderNumber != null && this._record.chiefsOrderNumber != undefined && this._record.chiefsOrderNumber != "")
            return this.chiefsOrderMailDeliveryOptions;
        else
            return this.normalMailDeliveryOptions;
    }

    @HostListener('window:load') hideLoader() {
        this.loaderService.toggleLoader(false);
    }


    @Input() set flowId(value: string) {
        this._flowId = value;
    }

    @ViewChild('envelopeType') envelopeType: ElementRef;
    letterDetailsForm: FormGroup;
    letterContentsForm: FormGroup;

    emptyRecipient = {
        title:"",
        name1: "",
        name2: "",
        name3: "",
        address1: "",
        address2: "",
        address3: "",
        city: "",
        state: "",
        zipCode: "",
        zipPlus4: ""
    } as LetterAddress;

    envelopeTypeDisabled: boolean = false;
    labelSizeOptions: Array<any>;
    saveTrackRre: boolean;
    labelSenderEmail: string;
    pdfLoaded: boolean = false;
    pdfSrc: string;
    sentDate: Date | null;
    deliveryDate: Date | null;
    daysPast: number = 2;
    daysFuture: number = 7;
    deliveredDaysPast: number = 2;
    deliveredDaysFuture: number = 7;
    letterGenerated = false;
    attachmentsFetched = false;
    editingLetter = false;
    letterEditButtonText = "Edit the Letter";
    htmlContentFetched: boolean = false;
    htmlContent: string = "";

    public _recordReady: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public _subscriptions: Subscription = new Subscription();
    public _record: Letter;
    public _flowId: string;
    public selectedOffice: LetterAddress;
    public _mailAttachments: LetterAttachment[] = [];
    public _displayedFiles: IUploadFile[] = [];

    get record(): Letter {
        return this._record;
    }
    previewUrl:SafeResourceUrl;
    viewUrl:SafeResourceUrl;

    get letterStatusName(): string {
        return LetterStatus[this.record.status];
    }

    constructor(
        private readonly authService: AuthService,
        public readonly lettersService: LettersService,
        public readonly _docSvc: DocumentManagementService,
        public readonly _reportService: ReportSelectorService,
        public readonly subheaderService: SubHeaderService,
        public readonly dateService: DateService,
        public readonly _notificationService: SystemNotificationService,
        public readonly router: Router,
        public readonly formBuilder: FormBuilder,
        public readonly loaderService: LoaderService,
        public readonly dialogService: DynamicDialogService,
        public sanitizer: DomSanitizer
    ) {
        this.router.routeReuseStrategy.shouldReuseRoute = () => false;
    }

    constructForm(): void {
        var matchingOffice = this.offices.find(x => x.address1 === this._record.office.address1);
        let officeTitle = '';

        let attachmentsToBeMerged = this._mailAttachments.filter(a => !a.shouldBeMergedIntoLetter);
        if (matchingOffice !== undefined)
            officeTitle = matchingOffice.title;
        this.letterDetailsForm = this.formBuilder.group({
            mailDeliveryType: [this._record.mailType, [Validators.required]],
            pageCount: [this._record.additionalPageCount],
            labelSize: new FormControl({ value: this._record.labelSize, disabled: this._record.labelSize == LabelSizeKind.Full9x12 }, Validators.required),
            originatingOffice: [officeTitle, [Validators.required]],
            recipientName1: [this._record.recipient.name1, [Validators.required]],
            organizationName: [this._record.recipient.name2, [Validators.required]],
            organizationId: [this._record.organizationId, [Validators.required]],
            address1: [this._record.recipient.address1, [Validators.required]],
            address2: [this._record.recipient.address2],
            city: [this._record.recipient.city, [Validators.required]],
            state: [this._record.recipient.state, [Validators.required]],
            zipCode: [this._record.recipient.zipCode, [Validators.required]],
            zipPlus4: [this._record.recipient.zipPlus4],
            refLine: [this._record.referenceLine],
            restrictDelivery: [this._record.restrictDelivery],
            trackRre: [this._record.trackRRE],
            notifySender: [this._record.notifySender],
            notificationEmail: this._record.notifySender && this._record.isCertified ? [this.authService.userEmail, [Validators.required, Validators.pattern(ValidatorPatterns.Email)]] : [this.authService.userEmail, Validators.pattern(ValidatorPatterns.Email)],
            uploadedFiles: [attachmentsToBeMerged.length > 0 ? attachmentsToBeMerged : [], []],
            otherReason: [this._record.otherReason],
            otherTrackingNumber: [this._record.trackingNumber],
        });

        this.letterContentsForm = this.formBuilder.group({
            updatedContents: [""],
        });
        this.labelSenderEmail = this._record.notifySender ? '*Sender Email' : 'Sender Email';
    }

    ngOnDestroy() {
        if (this._subscriptions) this._subscriptions.unsubscribe();
    }

    ngOnInit() {
        this.sentDate = this.dateService.generateDateWithOffset(0, 0, 0);
        this.deliveryDate = this.dateService.generateDateWithOffset(0, 0, 0);
        var record = this._recordReady.subscribe((ready) => {
            if (ready) {
                this.setDatePickerWindows(this._record.mailType);
                let docPromise: Promise<any>;
                if (this._record.pdfDocumentId === undefined || this._record.pdfDocumentId === null) {
                    docPromise = this._reportService.generateLetter(this._record).toPromise();
                } else {
                    docPromise = this._docSvc.getStoredDocument(this.record.onBaseDocumentTypeName, this.record.pdfDocumentId).toPromise();
                }
                docPromise.then((pdf) => {
                    this.letterGenerated = true;
                    this.pdfSrc = window.URL.createObjectURL(pdf);
                    this.pdfLoaded = true;
                    if (this.letterGenerated && this.attachmentsFetched)
                        this.loaderService.toggleLoader(false);
                }, (error) => { this.loaderService.toggleLoader(false); console.error(error); });

                let htmlContentPromise: Promise<any>;
                htmlContentPromise = this.lettersService.getHtmlContent(this._record.id, true).toPromise();
                htmlContentPromise.then((htmlContent) => {
                    this.htmlContent = htmlContent.html;
                    this.letterContentsForm.controls['updatedContents'].setValue(this.htmlContent);
                    this.htmlContentFetched = true;
                    if (this.letterGenerated)
                        this.loaderService.toggleLoader(false);
                }, (error) => { this.loaderService.toggleLoader(false); console.error(error); });

                let mailAttachmentsPromise: Promise<LetterAttachment[]>;
                mailAttachmentsPromise = this.lettersService.getMailAttachments(this._record.id).toPromise();
                mailAttachmentsPromise.then((attachments) => {
                    this._mailAttachments = attachments;

                    //Go through the attachments and create the respective uploads.
                    for (let x = 0; x < this._mailAttachments.length; x++) {
                        let letterAttachment = (this._mailAttachments[x]);
                        let convertedUploadedFile = {
                            completed: true,
                            blobUri: letterAttachment.blobUri,
                            isUploading: false,
                            sentToOnbase: letterAttachment.sentToOnbase,
                            documentType: letterAttachment.onbaseDocumentType,
                            type: letterAttachment.type,
                            fileName: letterAttachment.fileName,
                            percentage: 100,
                            canBeDeleted: !letterAttachment.sentToOnbase,
                            waiting: false,
                            rawFile: { lastModified: 0, name: letterAttachment.fileName, size: 0, type: letterAttachment.type } as File,
                        } as IUploadFile;

                        //on the letter page, only display the attachments that don't get merged in to the letter
                        if (!letterAttachment.shouldBeMergedIntoLetter && this._displayedFiles.filter(a => a.blobUri == letterAttachment.blobUri).length == 0) {
                            this._displayedFiles.push(convertedUploadedFile)

                            this.fileAdded(convertedUploadedFile);
                        }
                    }

                    this.attachmentsFetched = true;
                    if (this.letterGenerated)
                        this.loaderService.toggleLoader(false);
                }, (error) => { this.loaderService.toggleLoader(false); console.error(error); });
            }
        });

        this._subscriptions.add(record);
    }

    toggleLetterEditing(showDialog: boolean = true) {
        if (!this.editingLetter) {
            this.editingLetter = !this.editingLetter;
            this.letterEditButtonText = "Stop Editing";
        }
        else {
            if (showDialog) {
                this.stopEditingDialog.toggleDialog(true);
                this.dialogService.updateDialog(this.stopEditingDialog);
            }
            this.letterEditButtonText = "Edit the Letter";
        }
    }

    stopNow(event: Event): void {
        event.stopPropagation();
    }

    checkLabelSizeOptions(): void {
        let selectEnvelopeType = <HTMLSelectElement>this.envelopeType.nativeElement;
        if ((this._record.pageCount + this._record.additionalPageCount) > 10) {
            selectEnvelopeType.value = '1';
            this.letterDetailsForm.controls['labelSize'].enable();
        } else {
            this.letterDetailsForm.controls['labelSize'].disable();
        }
    }

    generateLabel(): void {
        //manually setting values to fix 27143
        if (this._record.status === LetterStatus.Pending) {
            this.setRecordValuesFromFormControls(false);

            this.loaderService.toggleLoader(true);

            this.htmlContentFetched = false;
            this.editingLetter = false;
            this.letterEditButtonText = "Edit the Letter";
            this.letterContentsForm.controls['updatedContents'].markAsPristine();
            let letterAttachments = this.buildAttachments();
            let letter = this.lettersService.saveWithAttachments(this._record, stripFroalaWatermark(this.htmlContent), letterAttachments).pipe(tap((results) => {
                this.setRecordValuesFromFormControls(true);                
            }),
            mergeMap(() => {
                return  this.lettersService.saveAndGenerateLabel(this._record);
            }),
            ).subscribe(l => {
                    this._record = l;
                    //Reset the pdf source
                    this.pdfSrc = "";
                    this._recordReady.next(true);
                    //this.loaderService.toggleLoader(false);
                }, (error) => {
                    console.error(error);
                    this.loaderService.toggleLoader(false);
                });
            this._subscriptions.add(letter);

            if (this._flowId != null && this._flowId != undefined) {
                let notificationDeletionResponse: any;
                let deleteNotificationResult = this._notificationService.deleteNotificationByFlowAndType(this._flowId, "Letter")
                    .subscribe(response => {
                        notificationDeletionResponse = response;
                    });
                this._subscriptions.add(deleteNotificationResult);
            }
        }
        else {
            Object.keys(this.letterDetailsForm.controls).forEach(field => {
                const control = this.letterDetailsForm.get(field);
                control.markAsTouched({ onlySelf: true });
            });

        }
    }

    setRecordValuesFromFormControls(markAsSent: boolean): void {

        if (markAsSent)
            this._record.status = LetterStatus.Sent;

        let officeTitle = this.letterDetailsForm.controls['originatingOffice'].value
        var officeAddress = this.offices.find(x => x.title === officeTitle);

        this._record.trackingNumber = this.letterDetailsForm.controls['otherTrackingNumber'].value;
        this._record.isCertified = this.letterDetailsForm.controls['mailDeliveryType'].value == MailDeliveryType.Certified
            || (this.letterDetailsForm.controls['mailDeliveryType'].value == MailDeliveryType.Other && (this._record.trackingNumber != null && this._record.trackingNumber != ""));
        this._record.mailType = this.letterDetailsForm.controls['mailDeliveryType'].value;
        this._record.additionalPageCount = this.letterDetailsForm.controls['pageCount'].value;
        this._record.labelSize = this.letterDetailsForm.controls['labelSize'].value;
        this._record.office = { ...officeAddress };
        this._record.organizationId = this.letterDetailsForm.controls['organizationId'].value;
        this._record.recipient.name1 = this.letterDetailsForm.controls['recipientName1'].value;
        this._record.recipient.name2 = this.letterDetailsForm.controls['organizationName'].value;
        this._record.recipient.address1 = this.letterDetailsForm.controls['address1'].value;
        this._record.recipient.address2 = this.letterDetailsForm.controls['address2'].value;
        this._record.recipient.city = this.letterDetailsForm.controls['city'].value;
        this._record.recipient.state = this.letterDetailsForm.controls['state'].value;
        this._record.recipient.zipCode = this.letterDetailsForm.controls['zipCode'].value;
        this._record.recipient.zipPlus4 = this.letterDetailsForm.controls['zipPlus4'].value;
        this._record.referenceLine = this.letterDetailsForm.controls['refLine'].value;
        this._record.restrictDelivery = this.letterDetailsForm.controls['restrictDelivery'].value;
        this._record.trackRRE = this.letterDetailsForm.controls['trackRre'].value;
        this._record.notifySender = this.letterDetailsForm.controls['notifySender'].value;
        this._record.notificationEmail = this.letterDetailsForm.controls['notificationEmail'].value;
        this._record.otherReason = this.letterDetailsForm.controls['otherReason'].value;

        //Even though touching the date sets it, also set it here to get the default value if they never touch it
        this._record.sentDate = this.sentDate;

        //Only set the delivery date from the form controls if the delivery type is hand delivery
        if (this.letterDetailsForm.controls['mailDeliveryType'].value == MailDeliveryType.HandDelivery)
            this._record.deliveryDate = this.deliveryDate;

    }

    regenerate() {
        if (this.record.status != LetterStatus.Pending) {
            this.loaderService.toggleLoader(true);
            let clone = this.lettersService.clone(this.record).subscribe(l => {
                this.loaderService.toggleLoader(false);
                this.router.navigate(['letters', l.id]);
            });
            this._subscriptions.add(clone);
        }
    }

    changeText(isSelected: boolean): void {
        this.labelSenderEmail = isSelected ? '*Sender Email' : 'Sender Email';

        if (!isSelected) {
            this.letterDetailsForm.controls['notificationEmail'].clearValidators();
            this.letterDetailsForm.controls['notificationEmail'].setValidators([Validators.pattern(ValidatorPatterns.Email)]);
        }
        else {
            this.letterDetailsForm.controls['notificationEmail'].clearValidators();
            this.letterDetailsForm.controls['notificationEmail'].setValidators([Validators.required, Validators.pattern(ValidatorPatterns.Email)]);
        }

        this.letterDetailsForm.controls['notificationEmail'].updateValueAndValidity();

    }

    sentDateChanged(model: Date): void {
        this.sentDate = model;
    }

    deliveryDateChanged(model: Date): void {
        this.deliveryDate = model
    }

    checkForUpdatedStatus() {     
        let updateLetterStatus = this.lettersService.checkForUpdatedLetterStatus(this._record.id).subscribe(l => {
            this.record = l;
        });
        this._subscriptions.add(updateLetterStatus);
    }


    @ViewChild('letterDownloadLink') public letterDownloadLink: ElementRef;
    downloadLetter(): any {
        let url = this._docSvc.downloadStoredDocument(this.record.onBaseDocumentTypeName, this.record.pdfDocumentId);
        const link = this.letterDownloadLink.nativeElement;
        link.href = url;
        link.click();

    }

    @ViewChild('labelDownloadLink') public labelDownloadLink: ElementRef;
    downloadLabel(): any {
        const link = this.labelDownloadLink.nativeElement;
        link.href = this.lettersService.associateDocumentUrl(this.record.id, AssociateDocumentKind.Label);
        link.click();
    }

    @ViewChild('deliveryDownloadLink') public deliveryDownloadLink: ElementRef;
    downloadDelivery(): any {
        const link = this.deliveryDownloadLink.nativeElement;
        link.href = this.lettersService.associateDocumentUrl(this.record.id, AssociateDocumentKind.Delivery);
        link.click();
    }

    @ViewChild('acceptanceDownloadLink') public acceptanceDownloadLink: ElementRef;
    downloadAcceptance(): any {
        const link = this.acceptanceDownloadLink.nativeElement;
        link.href = this.lettersService.associateDocumentUrl(this.record.id, AssociateDocumentKind.Acceptance);
        link.click();
    }

    @ViewChild('signatureDownloadLink') public signatureDownloadLink: ElementRef;
    downloadSignature(): any {
        const link = this.signatureDownloadLink.nativeElement;
        link.href = this.lettersService.associateDocumentUrl(this.record.id, AssociateDocumentKind.Signature);
        link.click();
    }

    setDatePickerWindows(deliveryType: string): void {
        //Since simple certified can only date a date sent from today through the next week, enforce that limit on the control
        if (deliveryType === MailDeliveryType.Certified) {
            if (this._sentDatePicker != undefined) {
                this._sentDatePicker.daysPast = 2;
                this._sentDatePicker.daysFuture = 7;
            }
        }
        //If not, give a wider window
        else {
            if (this._sentDatePicker != undefined) {
                this._sentDatePicker.daysPast = 30;
                this._sentDatePicker.daysFuture = 7;
            }
            else {
                this.daysPast = 30;
                this.daysFuture = 7;
            }

            if (this._deliveryDatePicker != undefined) {
                this._deliveryDatePicker.daysPast = 30;
                this._deliveryDatePicker.daysFuture = 7;
            }
            else {
                this.deliveredDaysPast = 30;
                this.deliveredDaysFuture = 7;
            }
        }
    }

    mailDeliveryTypeChanged(mailDeliveryTypeChanged: any): void {
        const uploadedFilesCtrl = this.letterDetailsForm.get("uploadedFiles");
        let mailDeliveryType = this.letterDetailsForm.controls['mailDeliveryType'].value

        if (uploadedFilesCtrl != null) {
            if (mailDeliveryType === MailDeliveryType.CertificateOfMailing) {
                uploadedFilesCtrl.setValidators([Validators.required, Validators.minLength(1)]);
            } else {
                uploadedFilesCtrl.clearValidators();
            }
        }

        this.setDatePickerWindows(mailDeliveryType);

        //empty out any other specific fields if the delivery method is no longer other
        if (mailDeliveryType !== MailDeliveryType.Other) {
            this.letterDetailsForm.controls['otherReason'].setValue("");
        }

        //Wipe out the delivery date if mail type is not hand delivery
        if (mailDeliveryType !== MailDeliveryType.HandDelivery) {
            this._record.deliveryDate = null;
        }

    }

    displayFileUploads(): boolean {
        let selectedDeliveryType = this.letterDetailsForm.controls['mailDeliveryType'].value;
        let displayUploads = selectedDeliveryType == MailDeliveryType.CertificateOfMailing || selectedDeliveryType == MailDeliveryType.HandDelivery
                                || selectedDeliveryType == MailDeliveryType.Other;

        return displayUploads;
    }

    onFileDeleted(evt: IUploadFile): void {
        const filesCtrl = this.letterDetailsForm.get("uploadedFiles");
        if (filesCtrl != null) {
            const filesList: IUploadFile[] = <IUploadFile[]>filesCtrl.value;
            const fileIndex = filesList.findIndex(l => l.fileName === evt.fileName);
            if (fileIndex !== -1) {
                filesList.splice(fileIndex, 1);
                filesCtrl.setValue(filesList);
            }
        }
    }

    fileAdded(evt: IUploadFile): void {
        const filesCtrl = this.letterDetailsForm.get("uploadedFiles");
        if (filesCtrl != null) {
            const filesList: IUploadFile[] = <IUploadFile[]>filesCtrl.value;
            const isNew = filesList.findIndex(l => l.fileName === evt.fileName) === -1;
            if (isNew) {
                filesList.push(evt);
                filesCtrl.setValue(filesList);
            }
        }

        //If this file isn't uploaded yet, add it to list
        if (this._displayedFiles.filter(a => a.blobUri == evt.blobUri).length == 0)
            this._displayedFiles.push(evt);
    }

    saveLetterConfirmation() {
        this.confirmLetterSaveDialog.toggleDialog(true);
        this.dialogService.updateDialog(this.confirmLetterSaveDialog);
    }

    isChiefsOrderLetter(): boolean {
        let isChiefsOrder = (this._record && this._record.chiefsOrderNumber != null && this._record.chiefsOrderNumber != undefined
                                && this._record.chiefsOrderNumber != "");
        return isChiefsOrder;
    }

    buildAttachments(): LetterAttachment[] {
        const filesCtrl = this.letterDetailsForm.get("uploadedFiles");
        let uploadedFiles = (<IUploadFile[]>filesCtrl.value);
        let letterAttachments: LetterAttachment[] = [];

        //Add back in any the pre-existing attachments that weren't tagged as needing merged in to letter
        //these are the ones that were provided earlier and can't be altered
        for (let x = 0; x < this._mailAttachments.length; x++) {

            if (this._mailAttachments[x].shouldBeMergedIntoLetter)
                letterAttachments.push(this._mailAttachments[x]);
        }

        for (let x = 0; x < uploadedFiles.length; x++) {
            let letterAttachment = new LetterAttachment(uploadedFiles[x], false);
            letterAttachment.letterId = this._record.id;

            switch (this._record.mailType) {
                case MailDeliveryType.CertificateOfMailing:
                    letterAttachment.onbaseDocumentType = "DNR - OG - LCO-Chief's Order - Certificate of Mailing"
                    break;
                case MailDeliveryType.HandDelivery:
                    if (this.isChiefsOrderLetter())
                        letterAttachment.onbaseDocumentType = "DNR - OG - LCO-Chief's Order - Delivery Confirmation"
                    else
                        letterAttachment.onbaseDocumentType = "DNR - OG - ADMLTR-General Letter Delivery Confirmation"
                    break;
                case MailDeliveryType.Other:
                    letterAttachment.onbaseDocumentType = "DNR - OG - LCO-Chief's Order - Mail Supporting Documentation"
                    break;
            }
            letterAttachments.push(letterAttachment);
        }

        return letterAttachments;
    }

    refreshFileUploadList() {

    }


    saveLetter(forcePreviewRegenerate: boolean = false): void {
        if (this._record.status === LetterStatus.Pending) {
            this.setRecordValuesFromFormControls(false);

            this.loaderService.toggleLoader(true);

            let letterAttachments = this.buildAttachments();

            let letter = this.lettersService.saveWithAttachments(this._record, stripFroalaWatermark(this.htmlContent), letterAttachments)
                .subscribe(l => {
                    this._mailAttachments = letterAttachments;

                    if (forcePreviewRegenerate) {
                        this.pdfLoaded = false;
                        this.htmlContentFetched = false;
                        this.editingLetter = false;
                        this.letterEditButtonText = "Edit the Letter";
                        this.letterContentsForm.controls['updatedContents'].markAsPristine();
                        this.record = l;
                    }
                    else {
                        this._record = l;
                        this.loaderService.toggleLoader(false);
                    }
                }, (error) => {
                    console.error(error);
                    this.loaderService.toggleLoader(false);
                });
            this._subscriptions.add(letter);
        }
    }

    onLetterSaveConfirm(): void {
        if (this.letterContentsForm.controls['updatedContents'].dirty)
            this.saveLetter(true);
        else
            this.saveLetter(false);

    }
}
